(function () {
  /** @type {Map<string, import('swiper').Swiper>} */
  const swiperInstances = new Map();

  /**
   * Find the real slide index for a given media ID within the modal Swiper.
   * @param {HTMLElement} swiperEl
   * @param {string} mediaId
   * @returns {number} – falls back to 0 if not found
   */
  function getSlideIndexForMediaId(swiperEl, mediaId) {
    const realSlides = swiperEl.querySelectorAll(
      '.swiper-slide:not(.swiper-slide-duplicate)'
    );
    let index = 0;
    realSlides.forEach((slide, i) => {
      if (slide.dataset.mediaId === mediaId) index = i;
    });
    return index;
  }

  /**
   * Returns the real (non-clone) slide at swiper.realIndex.
   * @param {HTMLElement} swiperEl
   * @param {import('swiper').Swiper} swiper
   * @returns {Element|null}
   */
  function getActiveSlide(swiperEl, swiper) {
    const realSlides = swiperEl.querySelectorAll(
      '.swiper-slide:not(.swiper-slide-duplicate)'
    );
    return realSlides[swiper.realIndex] ?? null;
  }

  /**
   * Pause the video in a slide, if one has been loaded from its <template>.
   * Does nothing if the user hasn't clicked play yet (video not in DOM).
   * @param {Element} slide
   */
  function pauseSlideVideo(slide) {
    slide?.querySelector('video')?.pause();
  }

  /**
   * Resume the video in a slide if it was mid-play when the user navigated away.
   * Does nothing if the user hasn't interacted with it yet (currentTime === 0).
   * @param {Element} slide
   */
  function resumeSlideVideo(slide) {
    const video = slide?.querySelector('video');
    if (video && video.paused && video.currentTime > 0) {
      video.play().catch(() => {});
    }
  }

  /**
   * Pause all videos inside the modal.
   * Returns the set of video elements that were actively playing.
   * @param {HTMLElement} modalEl
   * @returns {Set<HTMLVideoElement>}
   */
  function pauseAllModalVideos(modalEl) {
    const wasPlaying = new Set();
    modalEl.querySelectorAll('video').forEach((v) => {
      if (!v.paused) {
        wasPlaying.add(v);
        v.pause();
      }
    });
    return wasPlaying;
  }

  /**
   * Open the modal and jump to the slide matching the given media ID.
   * @param {HTMLElement} modalEl
   * @param {string} mediaId
   */
  function openModal(modalEl, mediaId) {
    const sectionId = modalEl.id.replace('ProductModal-', '');
    const swiperEl = modalEl.querySelector(`#ProductModalSwiper-${sectionId}`);
    if (!swiperEl) return;

    modalEl.setAttribute('open', '');
    modalEl.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    const dialog = modalEl.querySelector('[role="dialog"]');
    if (dialog) dialog.focus();

    if (!swiperInstances.has(sectionId)) {
      const paginationEl = swiperEl.querySelector('[data-custom-pagination]');
      const dots = paginationEl.querySelectorAll('.custom-dot');

      const swiper = new Swiper(swiperEl, {
        loop: true,
        keyboard: { enabled: true },
        navigation: {
          nextEl: swiperEl.querySelector('.product-media-modal__next'),
          prevEl: swiperEl.querySelector('.product-media-modal__prev'),
        },
        a11y: {
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
        },
        on: {
          slideChange(swiper) {
            const leaving = getActiveSlide(swiperEl, swiper);
            pauseSlideVideo(leaving);

            const realIndex = swiper.realIndex;
            dots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === realIndex);
            });
          },
          slideChangeTransitionEnd(swiper) {
            const arriving = getActiveSlide(swiperEl, swiper);
            resumeSlideVideo(arriving);
          },
        },
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          swiper.slideToLoop(Number(dot.dataset.slideIndex));
        });
      });

      swiperInstances.set(sectionId, swiper);
    }

    const swiper = swiperInstances.get(sectionId);
    const index = getSlideIndexForMediaId(swiperEl, mediaId);
    swiper.slideTo(index, 0);
  }

  /**
   * Close the modal, pause all modal videos, and restore any
   * PDP videos that were playing before the modal opened.
   * @param {HTMLElement} modalEl
   */
  function closeModal(modalEl) {
    modalEl.removeAttribute('open');
    modalEl.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    pauseAllModalVideos(modalEl);

    const pdpPlaying = modalEl._pdpVideosPlaying;
    if (pdpPlaying?.size) {
      pdpPlaying.forEach((v) => v.play().catch(() => {}));
      modalEl._pdpVideosPlaying = null;
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.product__media-zoom-trigger');
    if (trigger) {
      const modalSelector = trigger.dataset.modal;
      const mediaId = trigger.dataset.mediaId;
      const modalEl = document.querySelector(modalSelector);
      if (modalEl) {
        event.preventDefault();

        const pdpPlaying = new Set();
        document.querySelectorAll('video').forEach((v) => {
          if (!modalEl.contains(v) && !v.paused) pdpPlaying.add(v);
        });
        modalEl._pdpVideosPlaying = pdpPlaying;

        openModal(modalEl, mediaId);
      }
      return;
    }

    const closeBtn = event.target.closest('.product-media-modal__toggle');
    if (closeBtn) {
      const modalEl = closeBtn.closest('product-modal');
      if (modalEl) closeModal(modalEl);
      return;
    }

    const modal = event.target.closest('product-modal');
    if (modal && !event.target.closest('.product-media-modal__dialog')) {
      closeModal(modal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('product-modal[open]').forEach(closeModal);
  });
})();